import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';

const CookiePolicyPage: React.FC = () => {
  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className="pt-24 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Política de Cookies
          </h1>

          <p className="text-gray-600 mb-8">
            Última atualização: {new Date().toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="bg-white rounded-xl shadow-soft p-8 md:p-10 space-y-8">
            {/* Introdução */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. O que são Cookies?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies são pequenos ficheiros de texto que são armazenados no seu dispositivo (computador, tablet ou
                telemóvel) quando visita um website. Os cookies permitem que o website reconheça o seu dispositivo e
                memorize informações sobre a sua visita, como as suas preferências e configurações.
              </p>
              <p className="text-gray-700 leading-relaxed">
                A <strong>2Gf - Innovation Systems, Lda</strong>, através da sua plataforma <strong>Vulpes</strong>,
                utiliza cookies e tecnologias similares para melhorar a sua experiência de navegação, personalizar
                conteúdos e analisar o tráfego do website.
              </p>
            </section>

            {/* Tipos de Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Tipos de Cookies que Utilizamos</h2>

              <div className="space-y-6">
                {/* Cookies Essenciais */}
                <div className="border-l-4 border-green-500 pl-6 bg-green-50 p-4 rounded-r-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🔒</span>
                    2.1. Cookies Estritamente Necessários
                  </h3>
                  <p className="text-gray-700 mb-3">
                    Estes cookies são essenciais para o funcionamento do website e não podem ser desativados.
                    Permitem funcionalidades básicas como autenticação, segurança e gestão de sessão.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Exemplos:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>Cookies de autenticação (login de utilizador)</li>
                      <li>Cookies de segurança e proteção contra fraude</li>
                      <li>Cookies de gestão de sessão</li>
                      <li>Cookies de preferências de privacidade</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3">
                      <strong>Base legal:</strong> Interesse legítimo (funcionamento do website)
                    </p>
                  </div>
                </div>

                {/* Cookies Funcionais */}
                <div className="border-l-4 border-blue-500 pl-6 bg-blue-50 p-4 rounded-r-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">⚙️</span>
                    2.2. Cookies de Funcionalidade
                  </h3>
                  <p className="text-gray-700 mb-3">
                    Estes cookies permitem que o website se lembre das escolhas que faz (como idioma, região ou
                    preferências) e fornecem funcionalidades melhoradas e personalizadas.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Exemplos:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>Preferências de idioma e região</li>
                      <li>Configurações de interface do utilizador</li>
                      <li>Estado de elementos interativos (menus expandidos, etc.)</li>
                      <li>Vídeos e chat ao vivo</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3">
                      <strong>Base legal:</strong> Consentimento do utilizador
                    </p>
                  </div>
                </div>

                {/* Cookies Analíticos */}
                <div className="border-l-4 border-purple-500 pl-6 bg-purple-50 p-4 rounded-r-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    2.3. Cookies de Análise e Performance
                  </h3>
                  <p className="text-gray-700 mb-3">
                    Estes cookies ajudam-nos a compreender como os visitantes interagem com o website, recolhendo
                    e reportando informações de forma anónima. Permitem-nos melhorar o funcionamento do website.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Exemplos:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>Google Analytics (métricas de tráfego e comportamento)</li>
                      <li>Análise de performance do website</li>
                      <li>Testes A/B e otimização</li>
                      <li>Monitorização de erros técnicos</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3">
                      <strong>Base legal:</strong> Consentimento do utilizador
                    </p>
                  </div>
                </div>

                {/* Cookies de Marketing */}
                <div className="border-l-4 border-orange-500 pl-6 bg-orange-50 p-4 rounded-r-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🎯</span>
                    2.4. Cookies de Marketing e Publicidade
                  </h3>
                  <p className="text-gray-700 mb-3">
                    Estes cookies são utilizados para apresentar anúncios relevantes para si e os seus interesses.
                    Também ajudam a limitar o número de vezes que vê um anúncio e a medir a eficácia de campanhas.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Exemplos:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>Remarketing e retargeting</li>
                      <li>Publicidade personalizada</li>
                      <li>Medição de conversões</li>
                      <li>Redes sociais (Facebook Pixel, LinkedIn Insights)</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3">
                      <strong>Base legal:</strong> Consentimento do utilizador
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Cookies de Terceiros */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Cookies de Terceiros</h2>
              <p className="text-gray-700 mb-4">
                Utilizamos serviços de terceiros que podem colocar cookies no seu dispositivo. Estes terceiros
                processam dados em nosso nome e de acordo com as nossas instruções:
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Serviço</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Finalidade</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Mais Informações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-700">Google Analytics</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Análise de tráfego</td>
                      <td className="px-6 py-4 text-sm">
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                          Ver política
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-700">Formspree</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Gestão de formulários</td>
                      <td className="px-6 py-4 text-sm">
                        <a href="https://formspree.io/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                          Ver política
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Duração */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Duração dos Cookies</h2>
              <p className="text-gray-700 mb-4">
                Os cookies podem ser temporários (de sessão) ou permanentes (persistentes):
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">⏱️</span>
                    Cookies de Sessão
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    São temporários e são eliminados quando fecha o navegador. Utilizados para manter a sua sessão
                    de login ativa durante a navegação.
                  </p>
                  <p className="text-xs text-gray-600">
                    <strong>Duração:</strong> Até fechar o navegador
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">📅</span>
                    Cookies Persistentes
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    Permanecem no seu dispositivo por um período de tempo definido ou até os eliminar manualmente.
                    Utilizados para lembrar preferências e melhorar a experiência.
                  </p>
                  <p className="text-xs text-gray-600">
                    <strong>Duração:</strong> De 30 dias a 2 anos (dependendo do tipo)
                  </p>
                </div>
              </div>
            </section>

            {/* Gestão de Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Como Gerir e Desativar Cookies</h2>
              <p className="text-gray-700 mb-4">
                Pode gerir ou desativar cookies através das definições do seu navegador. No entanto, desativar
                cookies essenciais pode afetar a funcionalidade do website.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-6">
                <p className="text-sm text-gray-800">
                  <strong>⚠️ Nota:</strong> Ao desativar cookies, algumas funcionalidades do website podem não
                  funcionar corretamente, incluindo o login e personalização de preferências.
                </p>
              </div>

              <h3 className="font-semibold text-gray-900 mb-3">Instruções por Navegador:</h3>

              <div className="space-y-3">
                <details className="bg-gray-50 p-4 rounded-lg">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-primary-600">
                    Google Chrome
                  </summary>
                  <p className="text-sm text-gray-700 mt-3">
                    Definições → Privacidade e segurança → Cookies e outros dados de sites → Gerir cookies
                  </p>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">
                    Ver instruções completas →
                  </a>
                </details>

                <details className="bg-gray-50 p-4 rounded-lg">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-primary-600">
                    Mozilla Firefox
                  </summary>
                  <p className="text-sm text-gray-700 mt-3">
                    Opções → Privacidade e Segurança → Cookies e dados de sites
                  </p>
                  <a href="https://support.mozilla.org/pt-PT/kb/cookies" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">
                    Ver instruções completas →
                  </a>
                </details>

                <details className="bg-gray-50 p-4 rounded-lg">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-primary-600">
                    Safari
                  </summary>
                  <p className="text-sm text-gray-700 mt-3">
                    Preferências → Privacidade → Gerir dados de websites
                  </p>
                  <a href="https://support.apple.com/pt-pt/HT201265" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">
                    Ver instruções completas →
                  </a>
                </details>

                <details className="bg-gray-50 p-4 rounded-lg">
                  <summary className="font-semibold text-gray-900 cursor-pointer hover:text-primary-600">
                    Microsoft Edge
                  </summary>
                  <p className="text-sm text-gray-700 mt-3">
                    Definições → Privacidade, pesquisa e serviços → Cookies e permissões de site
                  </p>
                  <a href="https://support.microsoft.com/pt-pt/microsoft-edge/eliminar-cookies-no-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:underline">
                    Ver instruções completas →
                  </a>
                </details>
              </div>
            </section>

            {/* Consentimento */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Consentimento e Retirada</h2>
              <p className="text-gray-700 mb-4">
                Ao visitar o nosso website pela primeira vez, será apresentado um banner de cookies onde pode
                aceitar ou rejeitar diferentes categorias de cookies (exceto os estritamente necessários).
              </p>

              <div className="bg-primary-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Pode alterar as suas preferências a qualquer momento:</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Através do painel de preferências de cookies no rodapé do website</li>
                  <li>Eliminando cookies diretamente nas configurações do navegador</li>
                  <li>Contactando-nos através de hello@vulpes.pt</li>
                </ul>
              </div>
            </section>

            {/* Atualizações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Atualizações desta Política</h2>
              <p className="text-gray-700">
                Podemos atualizar esta Política de Cookies periodicamente para refletir alterações nas nossas
                práticas ou por outros motivos operacionais, legais ou regulamentares. Recomendamos que reveja
                esta página regularmente. A data da última atualização está no topo desta página.
              </p>
            </section>

            {/* Mais Informações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Mais Informações</h2>
              <p className="text-gray-700 mb-4">
                Para mais informações sobre cookies e como os gerir, consulte:
              </p>

              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    AllAboutCookies.org
                  </a> - Guia completo sobre cookies
                </li>
                <li>
                  <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    CNPD - Comissão Nacional de Proteção de Dados
                  </a>
                </li>
                <li>
                  <a href="https://www.youronlinechoices.com/pt/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    Your Online Choices
                  </a> - Controlo de publicidade comportamental
                </li>
              </ul>
            </section>

            {/* Contacto */}
            <section className="border-t pt-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contacto</h2>
              <p className="text-gray-700 mb-4">
                Se tiver dúvidas sobre a nossa utilização de cookies, contacte-nos:
              </p>

              <div className="bg-primary-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>2Gf - Innovation Systems, Lda (Vulpes)</strong></p>
                <p className="text-gray-700 mb-2">Email: hello@vulpes.pt</p>
                <p className="text-gray-700 mb-2">Telefone: +351 969 059 898</p>
                <p className="text-gray-700">Morada: Covilhã, Portugal</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default CookiePolicyPage;
